import { fetchAuthSession } from 'aws-amplify/auth';
import { stringTrim } from 'lib/requestDataStringTrim';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { APIError, API_URL, ApiResponse, genericServiceError, getHeaders } from '../lib/_api-helpers';

type RequestMethod = 'PUT' | 'PATCH' | 'POST';

type UseModifyRequestData<T, U> = {
    modifyData: (requestBody: T) => Promise<ApiResponse<U>>;
    loading: boolean;
    errors: APIError[];
};

export default function useModifyRequest<T, U>(endpoint: string, method: RequestMethod): UseModifyRequestData<T, U> {
    const navigate = useNavigate();

    const url = `${API_URL}/${endpoint}`;
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState<APIError[]>([]);

    const modifyData = async <T>(requestBody: T): Promise<ApiResponse<U>> => {
        setLoading(true);
        setErrors([]);

        let token: string | undefined;

        try {
            const session = await fetchAuthSession();
            token = session.tokens?.accessToken?.toString();
            if (!token) {
                navigate('/signin');
                return genericServiceError<U>();
            }
        } catch {
            navigate('/signin');
            return genericServiceError<U>();
        }

        try {
            const headers = getHeaders(token);
            const trimmedBody = stringTrim(requestBody);

            const response = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(trimmedBody),
            });

            const result: ApiResponse<U> = await response.json();

            setErrors(result.errors ?? []);
            return result;
        } catch (err) {
            setErrors([{ field: 'global', code: 'serviceError' }]);
            return genericServiceError<U>();
        } finally {
            setLoading(false);
        }
    };

    return { modifyData, loading, errors };
}
